<script setup>
  import {inject, ref} from "vue";

  import ComponentCard from "@components/Cards/Card.vue";
  import ComponentEmptyState from "@components/EmptyState/EmptyState.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import {useForm} from "@inertiajs/vue3";
  import PatientsPatientAttributesDegreeOfCareAttribute from "@pages/Patients/Components/PatientAttributes/DegreeOfCareAttribute.vue";
  import PatientsPatientAttributesMobilityAttribute from "@pages/Patients/Components/PatientAttributes/MobilityAttribute.vue";
  import PatientsPatientAttributesNursingHomeAttribute from "@pages/Patients/Components/PatientAttributes/NursingHomeAttribute.vue";
  import PatientsPatientAttributesPatientAddressAttribute from "@pages/Patients/Components/PatientAttributes/PatientAddressAttribute.vue";
  import PatientsPatientAttributesSimpleAttribute from "@pages/Patients/Components/PatientAttributes/SimpleAttribute.vue";
  import PatientsPatientAttributesTubePatientAttribute from "@pages/Patients/Components/PatientAttributes/TubePatientAttribute.vue";
  import {debounce} from "es-toolkit";

  defineOptions({
    name: "PatientsPatientAttributesData",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
    withinDialog: {
      type: Boolean,
      default: false,
    },
  });

  const broadcast = inject("$broadcast");

  const recentlyAdded = ref(null);
  const form = useForm({
    type: null,
  });

  function getComponentName(type) {
    switch (type) {
      case "degreeOfCare":
        return PatientsPatientAttributesDegreeOfCareAttribute;
      case "mobility":
        return PatientsPatientAttributesMobilityAttribute;
      case "nursingHome":
        return PatientsPatientAttributesNursingHomeAttribute;
      case "patientAddress":
        return PatientsPatientAttributesPatientAddressAttribute;
      case "tubePatient":
        return PatientsPatientAttributesTubePatientAttribute;
      case "insulinPump":
      case "palliativePatient":
      case "port":
      case "stoma":
        return PatientsPatientAttributesSimpleAttribute;
    }
  }

  function alreadyExists(type) {
    return props.patient.patient_attributes.some((patient_attribute) => patient_attribute.type === type);
  }

  function add(type) {
    // add() is an exposed function; in this case we allow a type parameter...
    if (type !== undefined) {
      form.type = type;
    }
    if (alreadyExists(form.type)) {
      const target = document.querySelector("#attribute-" + form.type + " section");
      if (!props.withinDialog) {
        target.scrollIntoView({behavior: "smooth", block: "center"});
      }
      setTimeout(() => {
        target.classList.add("animate-glow");
        setTimeout(() => {
          target.classList.remove("animate-glow");
        }, 2000);
      }, 200);
      form.type = null;
    } else {
      form.post(route("attributes.store", {patient: props.patient.id}), {
        preserveScroll: true,
        only: ["flash", "errors", "patient"],
        onSuccess: () => {
          setTimeout(() => {
            recentlyAdded.value = form.type;
            const target = document.querySelector("#attribute-" + form.type + " section");
            if (target) {
              if (!props.withinDialog) {
                target.scrollIntoView({behavior: "smooth", block: "center"});
              }
              setTimeout(() => {
                target.classList.add("animate-glow");
                setTimeout(() => {
                  target.classList.remove("animate-glow");
                }, 2000);
              }, 200);
            }

            form.type = null;
          }, 200);
          broadcast.patient.postMessage(props.patient.id, {action: "reload.patient"});
        },
      });
    }
  }

  const addWithDelay = debounce(() => {
    add();
  }, 500);

  defineExpose({
    add,
  });
</script>

<template>
  <ComponentCard
    id="patient-attributes-data"
    title="Individuelle Besonderheiten"
    subtitle="Patientenanschrift, zusätzliche Angaben, medizinische Besonderheiten"
    :class="{
      'rounded-lg border-2 border-gray-200': !withinDialog,
      '-m-4 shadow-none!': withinDialog,
      'animate-none!': patient?.patient_attributes?.length,
    }"
    :test-id="getTestId('patient-attributes-data')"
  >
    <form :data-test="getTestId('patient-attributes-data-card-form')" @submit.prevent="add">
      <div class="flex items-baseline justify-end space-x-4">
        <ComponentSelect
          v-model="form.type"
          class="w-52!"
          :show-placeholder-option="false"
          :nullable="false"
          :dísabled="form.processing || form.type"
          :test-id="getTestId('patient-attributes-data-card-form-type')"
          @change="addWithDelay()"
        >
          <option value="" :data-test="getTestId('patient-attributes-data-card-form-select-option-placeholder')">
            bitte auswählen
          </option>

          <option value="patientAddress" :data-test="getTestId('patient-attributes-data-card-form-select-option-0')">
            Patientenanschrift
            <template v-if="alreadyExists('patientAddress')">(bereits erfasst)</template>
          </option>

          <optgroup label="Zusätzliche Angaben">
            <option value="nursingHome" :data-test="getTestId('patient-attributes-data-card-form-select-option-1')">
              Heimpatient
              <template v-if="alreadyExists('nursingHome')">(bereits erfasst)</template>
            </option>

            <option value="degreeOfCare" :data-test="getTestId('patient-attributes-data-card-form-select-option-2')">
              Pflegestufe
              <template v-if="alreadyExists('degreeOfCare')">(bereits erfasst)</template>
            </option>

            <option
              value="palliativePatient"
              :data-test="getTestId('patient-attributes-data-card-form-select-option-3')"
            >
              Palliativpatient
              <template v-if="alreadyExists('palliativePatient')">(bereits erfasst)</template>
            </option>

            <option value="mobility" :data-test="getTestId('patient-attributes-data-card-form-select-option-4')">
              Eingeschränkte Mobilität
              <template v-if="alreadyExists('mobility')">(bereits erfasst)</template>
            </option>
          </optgroup>

          <optgroup label="Medizinische Besonderheiten">
            <option value="tubePatient" :data-test="getTestId('patient-attributes-data-card-form-select-option-5')">
              Sonde
              <template v-if="alreadyExists('tubePatient')">(bereits erfasst)</template>
            </option>

            <option value="port" :data-test="getTestId('patient-attributes-data-card-form-select-option-6')">
              Port
              <template v-if="alreadyExists('port')">(bereits erfasst)</template>
            </option>

            <option value="insulinPump" :data-test="getTestId('patient-attributes-data-card-form-select-option-7')">
              Insulinpumpe
              <template v-if="alreadyExists('insulinPump')">(bereits erfasst)</template>
            </option>

            <option value="stoma" :data-test="getTestId('patient-attributes-data-card-form-select-option-8')">
              Stoma
              <template v-if="alreadyExists('stoma')">(bereits erfasst)</template>
            </option>
          </optgroup>
        </ComponentSelect>
      </div>
    </form>

    <ul v-if="patient?.patient_attributes?.length" class="mt-4 space-y-4">
      <li v-for="attribute in patient.patient_attributes" :id="'attribute-' + attribute.type" :key="attribute.id">
        <component
          :is="getComponentName(attribute.type)"
          :attribute="attribute"
          :patient="patient"
          :recently-added="recentlyAdded === attribute.type"
          :test-id="getTestId('patient-attributes-data')"
        />
      </li>
    </ul>

    <ComponentEmptyState v-else :test-id="getTestId('patient-attributes-data-card')">
      Sie können optional individuelle Besonderheiten zu diesem Patienten über das Auswahlfeld hinzufügen.
    </ComponentEmptyState>
  </ComponentCard>
</template>
